/**
 * Group Quotes Custom Hooks
 *
 * Custom hooks wrapping TanStack Query for group booking quotes.
 * Follows the established pattern of encapsulating useQuery/useMutation in custom hooks.
 *
 * Pattern: Always wrap useQuery/useMutation in custom hooks rather than
 * calling them directly in components.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type {
  GenerateQuoteInput,
  GeneratedQuote,
  GroupQuoteFilters,
  GroupQuotesListResponse,
  ParsedInquiry,
  SavedQuote,
  SaveQuoteInput,
  UpdateQuoteInput,
  RegenerateTemplateInput,
  EmailDraft,
} from "../../pages/groups/types";

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch list of group quotes with optional filters
 * Returns empty list on 404 (no quotes exist yet or backend not deployed)
 */
export function useGroupQuotes(filters?: GroupQuoteFilters) {
  return useQuery<GroupQuotesListResponse>({
    queryKey: ["group-quotes", filters],
    queryFn: async () => {
      try {
        return await api.groupQuotes.list(filters);
      } catch (error: unknown) {
        // Treat 404 as "no quotes" rather than an error
        // This handles both "no data yet" and "backend not deployed" cases
        if (
          error &&
          typeof error === "object" &&
          "response" in error &&
          (error as { response?: { status?: number } }).response?.status === 404
        ) {
          return { quotes: [], total: 0 };
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch a single group quote by ID
 * Only fetches when id is provided
 */
export function useGroupQuote(id: string | null) {
  return useQuery<SavedQuote>({
    queryKey: ["group-quotes", id],
    queryFn: () => {
      if (!id) throw new Error("Quote ID is required");
      return api.groupQuotes.get(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Generate a new quote using AI
 * Does not invalidate cache as this doesn't save to server yet
 */
export function useGenerateQuote() {
  return useMutation<GeneratedQuote, Error, GenerateQuoteInput>({
    mutationFn: (input: GenerateQuoteInput) => api.groupQuotes.generate(input),
  });
}

/**
 * Save a new quote to the database
 * Invalidates the quotes list cache on success
 */
export function useSaveQuote() {
  const queryClient = useQueryClient();

  return useMutation<SavedQuote, Error, SaveQuoteInput>({
    mutationFn: (data: SaveQuoteInput) => api.groupQuotes.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-quotes"] });
    },
  });
}

/**
 * Update an existing quote
 * Invalidates both the list and the specific quote cache on success
 */
export function useUpdateQuote() {
  const queryClient = useQueryClient();

  return useMutation<
    SavedQuote,
    Error,
    { id: string; data: UpdateQuoteInput }
  >({
    mutationFn: ({ id, data }) => api.groupQuotes.update(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate the list
      queryClient.invalidateQueries({ queryKey: ["group-quotes"] });
      // Invalidate the specific quote
      queryClient.invalidateQueries({ queryKey: ["group-quotes", id] });
    },
  });
}

/**
 * Delete a quote
 * Invalidates the quotes list cache on success
 */
export function useDeleteQuote() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => api.groupQuotes.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-quotes"] });
    },
  });
}

/**
 * Regenerate email draft with fresh AI wording
 * Returns a new emailDraft with template, subject, html, and text
 */
export function useRegenerateTemplate() {
  return useMutation<EmailDraft, Error, RegenerateTemplateInput>({
    mutationFn: (input: RegenerateTemplateInput) =>
      api.groupQuotes.regenerateTemplate(input),
  });
}

/**
 * Parse guest inquiry email to extract booking details
 * Uses AI to identify dates, room counts, guest info, and special requirements
 */
export function useParseInquiry() {
  return useMutation<ParsedInquiry, Error, string>({
    mutationFn: (emailText: string) => api.groupQuotes.parseInquiry(emailText),
  });
}
